import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

interface Props<T> {
  items: T[];
  renderCard: (item: T, index: number) => ReactNode;
  /** Tailwind gap-2 = 8px */
  gap?: number;
}

/**
 * Window-virtualized responsive card grid for the Library.
 *
 * Only the visible rows (plus a small overscan) are kept in the DOM, so the
 * grid stays smooth even with thousands of cards. Mirrors the previous
 * `grid-cols-3 md:grid-cols-5` layout.
 */
export function VirtualizedCardGrid<T>({ items, renderCard, gap = 8 }: Props<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(3);
  const [colWidth, setColWidth] = useState(120);

  useEffect(() => {
    const update = () => {
      const el = containerRef.current;
      if (!el) return;
      const w = el.clientWidth;
      const desired = w >= 768 ? 5 : 3;
      setCols(desired);
      setColWidth(Math.floor((w - gap * (desired - 1)) / desired));
    };
    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [gap]);

  const rows = Math.ceil(items.length / cols);
  // Card height ≈ image (col * 1.5 aspect) + name/price/padding (~46px).
  const rowHeight = Math.round(colWidth * 1.5) + 46 + gap;

  const parentOffsetRef = useRef(0);
  useEffect(() => {
    const measure = () => {
      const el = containerRef.current;
      if (!el) return;
      parentOffsetRef.current = el.getBoundingClientRect().top + window.scrollY;
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: rows,
    estimateSize: () => rowHeight,
    overscan: 4,
    scrollMargin: parentOffsetRef.current,
  });

  return (
    <div ref={containerRef} className="mt-2 relative" style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map(vRow => {
        const start = vRow.index * cols;
        const slice = items.slice(start, start + cols);
        return (
          <div
            key={vRow.key}
            className="absolute left-0 right-0 grid"
            style={{
              top: 0,
              transform: `translateY(${vRow.start - virtualizer.options.scrollMargin}px)`,
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              gap,
              height: rowHeight - gap,
            }}
          >
            {slice.map((item, i) => (
              <div key={start + i}>{renderCard(item, start + i)}</div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
